<!-- eslint-disable -->
<template>
  <div class="relative flex flex-col justify-between h-full items-center">
    <c-input
      id="voice-textarea"
      ref="textareaRef"
      readonly
      v-model="displayText"
      class="w-full mt-52px flex-1 voice-textarea"
      :class="{ 'scrolled': isScrolled }"
      type="textarea"
      :maxlength="props.maxlength"
      :placeholder="_ct_('voice_start') || 'Click the microphone to start talking...'"
    />

    <div class="mb-40px text-center h-80px flex flex-col justify-end">
      <!-- 初始状态 - 麦克风图标 -->
      <div 
        v-if="uiState === 'idle' && displayText.length < 500"
        class="voice-control"
        @click="startListening"
      >
        <img
          src="./imgs/voice.svg"
          class="voice-icon mt-16px w-64px"
          alt="Start Listening"
        >
      </div>

      <!-- 监听中状态 -->
      <div
        class="listening-container"
      >
        <!-- 顶部状态提示 -->
        <div class="listening-status">
          <span
            v-if="displayText.length >= 500"
            class="text-14px text-999 c-muli"
          >
            {{ _ct_('voice_words') || '500 character limit reached' }}
          </span>
          <template v-else>
            <span
              v-if="uiState === 'finished'"
              class="text-14px text-999 c-muli"
            >
              {{ _ct_('voice_continue') || 'Click the button to continue talking...' }}
            </span>
            <template v-if="uiState === 'listening'">
              <span
                v-if="showCountdown"
                class="c-muli-bold text-16px"
              >{{ (_ct_('voice_time') || '${SECS} seconds left').replace('${SECS}', countdown) }}</span>
              <span
                v-else-if="!isSilent"
                class="c-muli-bold text-16px text-[#1f1f1f]"
              >{{ _ct_('voice_listening') || 'I am listening...' }}</span>
              <span
                v-else
                class="silence-warning c-muli-bold text-16px"
              >{{ _ct_('voice_stop') || "Didn't hear clearly, please try again" }}</span>
            </template>
          </template>
        </div>
        
        <!-- 动态图标区域 -->
        <div
          v-if="uiState === 'listening' && displayText.length < 500"
          class="icon-area"
        >
          <!-- 正常监听时的音量动画 -->
          <div 
            v-if="!isSilent"
            class="volume-animation"
          >
            <div class="volume-indicator">
              <WaveAnimation
                :volume="volumeLevel * 100" 
                class="wave-animation-container"
              >
                <div 
                  v-for="i in 5" 
                  :key="i" 
                  class="volume-bar"
                  :class="{ 'active': i <= activeBars }"
                />
              </waveanimation>
            </div>
          </div>
          
          <!-- 静音超时时的暂停图标 -->
          <div 
            v-else
            class="pause-icon"
            @click="handleIconClick"
          >
            <img
              src="./imgs/pause.svg"
              alt="Pause"
            >
          </div>
        </div>
      </div>
      
      <!-- 结束后的操作按钮 -->
      <div
        v-if="uiState === 'finished' || displayText.length >= 500"
        class="action-buttons mt-16px"
      >
        <div
          class="flex flex-col items-center justify-center"
          @click="clearText"
        >
          <c-icon
            name="failure"
            size="20"
          />
          <span class="text-14px c-muli-semibold mt-4px">{{ _ct_('voice_clearbutton') || 'Clear' }}</span>
        </div>
        <img
          v-if="displayText.length >= 500"
          src="./imgs/negative.svg"
          class="voice-icon"
          alt="Pause"
        >
        <img
          src="./imgs/voice.svg"
          class="voice-icon"
          alt="Start Listening"
          @click="startListening"
        >
        <div
          class="flex flex-col items-center justify-center"
          @click="saveText"
        >
          <c-icon
            name="success"
            size="20"
          />
          <span class="text-14px c-muli-semibold mt-4px">{{ _ct_('voice_donebutton') || 'Done' }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref, onBeforeUnmount, onMounted, watch } from 'vue';
import * as speechsdk from 'microsoft-cognitiveservices-speech-sdk';
import WaveAnimation from './WaveAnimation.vue';
import {handleDefaultSiteConfig} from '@/utils/config';

const emit = defineEmits(['saveText', 'handleDenied']);

const subscriptionId = 'C6YZthY66kKU5oywHiRJ5d2EwZX1Bq8jFjTTjvTZRSTRVC0VZLhOJQQJ99BBAC4f1cMXJ3w3AAAYACOGdvBx';
const region = 'westus';
const getLang = () => {
  switch(handleDefaultSiteConfig().langCode) {
    case 'en-GB': 
      return 'en-US';
    case 'de-DE': 
      return 'de-DE'; 
    case 'es-ES': 
      return 'es-ES'; 
    case 'nl-BE': 
      return 'nl-NL'; 
    case 'fr-FR': 
      return 'fr-FR'; 
    case 'it-IT': 
      return 'it-IT'; 
    case 'pl-PL': 
      return 'pl-PL'; 
    case 'sv-SE': 
      return 'sv-SE'; 
    default: 
      return 'en-US';
  }
};

// 状态管理
const displayText = ref('');
const uiState = ref('idle'); // 'idle' | 'listening' | 'finished'
const activeBars = ref(0);
const elapsedSeconds = ref(0);
const countdown = ref(10);
const showCountdown = ref(false);
const isSilent = ref(false);
const silenceDuration = ref(0); // 静音持续时间（秒）
const volumeLevel = ref(0);
const isScrolled = ref(false);
const textareaRef = ref(null);
const TIME= 60;

const _ct_ = window._ct_ || function () {};

// Azure 相关变量
let recognizer;
let audioContext;
let analyser;
let microphone;
let volumeInterval = null;
let timerInterval = null;
let silenceInterval = null;
const props = defineProps({
  maxlength: {
    type: Number
  }
});


// 开始监听
const startListening = async () => {
  let finalText = displayText.value;
  let tempText = "";
  try {
    // 初始化音量检测
    const permission = await setupVolumeMeter();
    if (!permission) {
      emit('handleDenied');
      return; 
    }
    window.cps?.('event', {
      event_name: "vioce_to_word",
      event_category: "write_review",
      event_action: "click",
      event_label: {},
      event_params: {
        button_type: 'voice'
      }
    });
    uiState.value = 'listening';
    isSilent.value = false;
    silenceDuration.value = 0;
    
    // 初始化 Azure 语音识别
    const speechConfig = speechsdk.SpeechConfig.fromSubscription(subscriptionId, region);
    speechConfig.speechRecognitionLanguage = getLang();
    speechConfig.setProfanity(speechsdk.ProfanityOption.Masked);
    const audioConfig = speechsdk.AudioConfig.fromDefaultMicrophoneInput();
    recognizer = new speechsdk.SpeechRecognizer(speechConfig, audioConfig);

    recognizer.recognizing = (s, e) => {
      if (e.result.text) {
        tempText = e.result.text;
        displayText.value = finalText + " " + tempText;
        isSilent.value = false;
        silenceDuration.value = 0; // 重置静音计时器
      }
    };

    recognizer.recognized = (s, e) => {
      if (e.result.reason === speechsdk.ResultReason.RecognizedSpeech) {
        finalText += " " + e.result.text; // 将确认的文本加入最终结果
        tempText = ""; // 清空临时文本
        displayText.value = finalText; // 更新显示
      }
    };

    recognizer.startContinuousRecognitionAsync(
      () => {
        console.log("Continuous recognition started");
        // 开始计时
        startTimer();
        // 开始静音检测
        startSilenceDetection();
      },
      err => {
        // console.error("Error starting recognition:", err);
        stopListening();
      }
    );
  } catch (error) {
    // console.error("Error starting listening:", error);
    stopListening();
  }
};

// 处理图标点击
const handleIconClick = () => {
  if (isSilent.value) {
    stopListening();
  }
};

// 更新音量显示
const updateVolume = () => {
  const dataArray = new Uint8Array(analyser.frequencyBinCount);
  analyser.getByteFrequencyData(dataArray);
  
  let sum = 0;
  for (let i = 0; i < dataArray.length; i++) {
    sum += dataArray[i];
  }
  const averageVolume = sum / dataArray.length;
  volumeLevel.value = averageVolume / 255; // 标准化到0-1范围
};
onMounted(()=> {
  document.querySelector('#voice-textarea textarea').addEventListener('scroll', handleScroll);
});

const handleScroll = (e) => {
  isScrolled.value = e.target.scrollTop > 0;
};

// 监听内容变化检查是否溢出
watch(displayText.value, () => {
  checkOverflow();
});
// 检查内容是否溢出
const checkOverflow = () => {
  if (!textareaRef.value) return;
  
  const textarea = textareaRef.value;
  if (textarea.scrollHeight > textarea.clientHeight) {
    // 内容溢出，根据滚动位置决定是否显示边框
    isScrolled.value = textarea.scrollTop > 0;
  } else {
    // 内容未溢出，确保不显示顶部边框
    isScrolled.value = false;
  }
};

// 初始化时检查一次
checkOverflow();

function cleanup () {
  clearInterval(volumeInterval);
  clearInterval(timerInterval);
  clearInterval(silenceInterval);
  countdown.value = TIME;
  showCountdown.value = false;
}
// 停止监听
const stopListening = () => {
  if (recognizer) {
    recognizer.stopContinuousRecognitionAsync(
      () => {
        recognizer.close();
        recognizer = null;
        cleanup();
      },
      err => {
        console.error("Error stopping recognition:", err);
        cleanup();
      }
    );
  } else {
    cleanup();
  }
  
  uiState.value = 'finished';
};

// 清空文本
const clearText = () => {
  displayText.value = '';
  uiState.value = 'idle';
  window.cps?.('event', {
    event_name: "vioce_to_word",
    event_category: "write_review",
    event_action: "click",
    event_label: {},
    event_params: {
      button_type: 'clear'
    }
  });
  clearInterval(volumeInterval);
  clearInterval(timerInterval);
  clearInterval(silenceInterval);
};

// 保存文本
const saveText = () => {
  // 这里可以添加保存逻辑，如API调用等
  emit('saveText', displayText.value);
  displayText.value = '';
  window.cps?.('event', {
    event_name: "vioce_to_word",
    event_category: "write_review",
    event_action: "click",
    event_label: {},
    event_params: {
      button_type: 'done'
    }
  });
  uiState.value = 'idle';
};


// 设置音量检测
const setupVolumeMeter = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    audioContext = new (window.AudioContext || window.webkitAudioContext)();
    analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    microphone = audioContext.createMediaStreamSource(stream);
    microphone.connect(analyser);
    
    // 开始检测音量
    volumeInterval = setInterval(updateVolume, 100);
    return true;
  } catch (err) {
    return false;
    
  }
};

// 开始静音检测
const startSilenceDetection = () => {
  silenceInterval = setInterval(() => {
    if (activeBars.value === 0) {
      silenceDuration.value += 0.1; // 每100ms检测一次
      
      // 2秒无语音则显示暂停状态
      if (silenceDuration.value >= 2) {
        isSilent.value = true;
      }
    } else {
      silenceDuration.value = 0;
      isSilent.value = false;
    }
  }, 100);
};

// 开始计时器
const startTimer = () => {
  elapsedSeconds.value = 0;
  timerInterval = setInterval(() => {
    elapsedSeconds.value++;
    
    // 检查是否达到50秒（显示最后10秒倒计时）
    if (elapsedSeconds.value >= TIME - 10 && !showCountdown.value) {
      showCountdown.value = true;
    }
    
    // 更新倒计时
    if (showCountdown.value) {
      countdown.value = TIME - elapsedSeconds.value;
    }
    
    // 达到1分钟自动停止
    if (elapsedSeconds.value >= TIME) {
      stopListening();
    }
  }, 1000);
};

// 组件卸载前清理
onBeforeUnmount(() => {
  stopListening();
});
</script>
<style scoped>
.voice-textarea :deep(textarea) {
  height: 100%
}
.voice-textarea :deep(.c-input__body){
  height:90%
}
.voice-textarea.scrolled{
  border-top: 1px solid #ECECEC !important
}
</style>

<style scoped>
.wave-animation-container {
  width: 120px;
  height: 60px;
  margin: 0 auto;
}
:deep(.c-input) {
  border: none;
}
:deep(.c-input__body) {
  padding: 24px;
  padding-top: 0;
}

/* 初始状态 */
.voice-control {
  cursor: pointer;
  display: inline-block;
}

.voice-icon {
  width: 64px;
  height: 64px;
  transition: transform 0.2s;
}

.voice-control:hover .voice-icon {
  transform: scale(1.1);
}

/* 监听中状态 */
.listening-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}

.listening-status {
  display: flex;
  align-items: center;
  gap: 15px;
  font-weight: bold;
}

.silence-warning {
  color: #999;
}

.icon-area {
  cursor: pointer;
}

/* 音量动画 */
.volume-animation {
  display: flex;
  justify-content: center;
}

.volume-indicator {
  display: flex;
  align-items: flex-end;
  height: 40px;
  gap: 4px;
}

.volume-bar {
  width: 8px;
  background-color: #e0e0e0;
  border-radius: 4px;
  transition: all 0.1s;
}

.volume-bar.active {
  background-color: #4a90e2;
}

.volume-bar:nth-child(1) { height: 30px; }
.volume-bar:nth-child(2) { height: 30px; }
.volume-bar:nth-child(3) { height: 30px; }
.volume-bar:nth-child(4) { height: 30px; }
.volume-bar:nth-child(5) { height: 30px; }

/* 暂停图标 */
.pause-icon img {
  width: 64px;
  height: 64px;
}

/* 操作按钮 */
.action-buttons {
  display: flex;
  gap: 20px;
  justify-content: center;
}

.btn-clear, .btn-done {
  padding: 8px 20px;
  border-radius: 20px;
  font-weight: bold;
  cursor: pointer;
  border: none;
}

.btn-clear {
  background-color: #f8f9fa;
  color: #333;
}

.btn-done {
  background-color: #4a90e2;
  color: white;
}
</style>